// Containment for a page that fails to load or render (#12239).
//
// Pages are lazy-loaded, so a chunk that a deploy has removed makes the import
// reject during render. Without a boundary here that rejection reaches the
// app-root boundary in App.tsx, which replaces the entire app, sidebar
// included, with a generic message. The stale-chunk listener
// (services/staleChunkReload.ts) usually reloads the tab first; this is what
// the user sees when it does not (offline, or a second failure within its
// window). Same shape as SettingsTabErrorBoundary.
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

type RouteErrorReporter = (
  error: Error,
  componentStack: string | null | undefined,
  pathname: string
) => void;

const reportRouteError: RouteErrorReporter = (
  error,
  componentStack,
  pathname
) => {
  // The path as a tag rather than extra, so Sentry can group and filter by it.
  Sentry.captureException(error, {
    tags: { routePath: pathname },
    extra: { componentStack },
  });
};

const RouteFallback = () => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 px-8 text-center">
    <div className="text-[#10141B] text-base font-semibold">
      Couldn&apos;t load this section.
    </div>
    <div className="text-[#6E7480] text-sm font-mono">
      Reloading the page usually fixes it.
    </div>
    <button
      onClick={() => window.location.reload()}
      className="mt-1 bg-gray-800 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-gray-700 transition-colors cursor-pointer"
    >
      Reload
    </button>
  </div>
);

const RouteErrorBoundary = ({
  children,
  onRouteError = reportRouteError,
}: {
  children: React.ReactNode;
  onRouteError?: RouteErrorReporter;
}) => {
  const { pathname } = useLocation();
  return (
    // Navigating to another path clears a failure. `resetKeys`, not `key`:
    // a key would remount every page on every path change (/chat/a to
    // /chat/b), where the router otherwise keeps the page mounted.
    <ErrorBoundary
      resetKeys={[pathname]}
      FallbackComponent={RouteFallback}
      onError={(error, info) =>
        onRouteError(error, info.componentStack, pathname)
      }
    >
      {children}
    </ErrorBoundary>
  );
};

export default RouteErrorBoundary;
